import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { EditableInput } from '@/components/ui/editable-input';
import { EditableSelect } from '@/components/ui/editable-select';
import { CharacterCount } from '@/components/ui/character-count';
import { FormField } from '@/components/FormField';
import { post } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import {
  TitleFunction,
  TITLE_FUNCTION_LABELS,
  CreateTitleRequest,
  Title,
  isValidTitleFunction,
  TITLE_VALIDATION,
} from '@promo/shared';

interface AddTitleDialogProps {
  open: boolean;
  onClose: () => void;
  onTitleCreated: (title: Title) => void;
}

const functionOptions = Object.entries(TITLE_FUNCTION_LABELS).map(([value, label]) => ({
  value,
  label,
}));

export function AddTitleDialog({ open, onClose, onTitleCreated }: AddTitleDialogProps) {
  const [titleName, setTitleName] = useState('');
  const [titleFunction, setTitleFunction] = useState<TitleFunction>(TitleFunction.ENGINEERING);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const handleSubmit = async () => {
    if (!titleName.trim()) {
      setError('Title name is required');
      return;
    }

    if (titleName.length > TITLE_VALIDATION.MAX_NAME_LENGTH) {
      setError(`Title name must be ${TITLE_VALIDATION.MAX_NAME_LENGTH} characters or less`);
      return;
    }

    if (!isValidTitleFunction(titleFunction)) {
      setError('Please select a valid function');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const createRequest: CreateTitleRequest = {
        name: titleName.trim(),
        function: titleFunction,
      };

      const newTitle = await post<Title>('/titles', createRequest);

      // Invalidate titles cache to include the new title
      await queryClient.invalidateQueries({ queryKey: ['titles'] });

      // Reset form
      setTitleName('');
      setTitleFunction(TitleFunction.ENGINEERING);

      // Notify parent component
      onTitleCreated(newTitle);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create title');
      console.error('Failed to create title:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setTitleName('');
    setTitleFunction(TitleFunction.ENGINEERING);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent className="sm:max-w-[425px] bg-background border border-input shadow-lg">
        <DialogHeader>
          <DialogTitle>Add New Title</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Title Name Field */}
          <FormField label="Title Name" htmlFor="title-name">
            <EditableInput
              id="title-name"
              value={titleName}
              onChange={(e) => setTitleName(e.target.value)}
              placeholder="Enter title name"
              disabled={isSubmitting}
              maxLength={TITLE_VALIDATION.MAX_NAME_LENGTH}
              className="w-full"
            />
            <CharacterCount current={titleName.length} max={TITLE_VALIDATION.MAX_NAME_LENGTH} />
          </FormField>

          {/* Function Selection */}
          <FormField label="Function" htmlFor="title-function">
            <EditableSelect
              id="title-function"
              value={titleFunction}
              onValueChange={(value) => setTitleFunction(value as TitleFunction)}
              options={functionOptions}
              disabled={isSubmitting}
              className="w-full"
            />
          </FormField>

          {/* Error Message */}
          {error && <div className="text-sm text-red-500 bg-red-50 p-2 rounded">{error}</div>}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !titleName.trim()}>
            {isSubmitting ? 'Creating...' : 'Create Title'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
